import { TORONTO_SLOTS, TORONTO_TZ, detectTimeZone, formatSlotInZone, torontoToUtc, torontoToday } from "@/lib/lesson-slots";
import { zoneOptions } from "@/components/landing/cohort-time-converter";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2 } from "lucide-react";

import { bookAssessment } from "@/lib/assessment.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SiteFooter } from "@/components/site-chrome";
import logo from "@/assets/smart-word-labs-logo.png.asset.json";
import { breadcrumbJsonLd, SITE_URL, BRAND_OG_IMAGE, BRAND_OG_IMAGE_ALT, SITE_NAME } from "@/lib/seo";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/book")({
  head: () => ({
    meta: [
      { title: "Book a Free Assessment | Smart Word Labs" },
      {
        name: "description",
        content:
          "Book a free online English assessment for your child in under a minute. No account or payment details needed.",
      },
      { property: "og:title", content: "Book a Free Assessment | Smart Word Labs" },
      {
        property: "og:description",
        content: "Free 1-on-1 assessment and written learning plan for children aged 4-12.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/book` },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:image", content: BRAND_OG_IMAGE },
      { property: "og:image:alt", content: BRAND_OG_IMAGE_ALT },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: BRAND_OG_IMAGE },
      { name: "twitter:image:alt", content: BRAND_OG_IMAGE_ALT },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/book` }],
    scripts: [
      breadcrumbJsonLd([{ name: "Book a Free Assessment", path: "/book" }]),
    ],
  }),
  component: BookPage,
});

const slots = TORONTO_SLOTS;

function torontoToUtcIso(date: string, time: string) {
  return torontoToUtc(date, time).toISOString();
}

function BookPage() {
  const { t } = useTranslation("pages");
  const submit = useServerFn(bookAssessment);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [parentName, setParentName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [childName, setChildName] = useState("");
  const [childAge, setChildAge] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState(slots[0]!);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [zone, setZone] = useState(TORONTO_TZ);
  useEffect(() => setZone(detectTimeZone()), []);
  const zoneLabel = zoneOptions(zone).find((z) => z.value === zone)?.label ?? zone;

  const today = new Date().toISOString().slice(0, 10);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await submit({
        data: {
          parentName: parentName.trim(),
          parentEmail: parentEmail.trim(),
          childName: childName.trim(),
          childAge: Number(childAge),
          requestedAt: torontoToUtcIso(date, time),
          origin: window.location.origin,
          websiteUrl,
        },
      });
      setDone(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("book.errors.generic"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-secondary/40">
      <header className="bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-5 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={logo.url}
              alt="Smart Word Labs"
              width={1920}
              height={1047}
              decoding="async"
              className="h-9 w-auto object-contain"
            />
            <span className="sr-only">{t("book.brand")}</span>
          </Link>
        </div>
      </header>

      <Breadcrumbs trail={[{ name: t("book.breadcrumb"), path: "/book" }]} />

      <main className="mx-auto w-full max-w-lg flex-1 px-5 py-12">
        {done ? (
          <div className="surface-card p-8 text-center">
            <CheckCircle2 className="mx-auto size-12 text-accent" />
            <h1 className="mt-5 text-3xl text-primary">{t("book.success.title")}</h1>
            <p className="mt-3 text-muted-foreground">
              {t("book.success.body")}{" "}
              <strong className="text-primary">{parentEmail}</strong>{" "}
              {t("book.success.bodyEnd")}
            </p>
            <Button variant="hero" size="lg" className="mt-7" asChild>
              <Link to="/">{t("book.success.backHome")}</Link>
            </Button>
          </div>
        ) : (
          <div className="surface-card p-8">
            <CalendarCheck className="size-8 text-accent" />
            <h1 className="mt-4 text-3xl text-primary">{t("book.form.title")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("book.form.subtitle")}
            </p>

            <form onSubmit={onSubmit} className="mt-7 space-y-4">
              <div className="absolute left-[-9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
                <label htmlFor="book-website">Website</label>
                <input
                  id="book-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="parentName">{t("book.form.parentName")}</Label>
                <Input
                  id="parentName"
                  value={parentName}
                  onChange={(e) => setParentName(e.target.value)}
                  maxLength={100}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="parentEmail">{t("book.form.parentEmail")}</Label>
                <Input
                  id="parentEmail"
                  type="email"
                  value={parentEmail}
                  onChange={(e) => setParentEmail(e.target.value)}
                  maxLength={255}
                  required
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
                <div className="space-y-2">
                  <Label htmlFor="childName">{t("book.form.childName")}</Label>
                  <Input
                    id="childName"
                    value={childName}
                    onChange={(e) => setChildName(e.target.value)}
                    maxLength={100}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="childAge">{t("book.form.childAge")}</Label>
                  <Input
                    id="childAge"
                    type="number"
                    min={3}
                    max={14}
                    value={childAge}
                    onChange={(e) => setChildAge(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="date">{t("book.form.date")}</Label>
                  <Input
                    id="date"
                    type="date"
                    min={today}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time">{t("book.form.time")} ({zoneLabel})</Label>
                  <select
                    id="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground transition-colors focus:border-accent focus:outline-none"
                  >
                    {slots.map((s) => (
                      <option key={s} value={s}>
                        {formatSlotInZone(date || torontoToday(), s, zone)}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground">{t("book.form.localTimeNote")}</p>
                </div>
              </div>

              <Button type="submit" variant="hero" size="lg" className="w-full" disabled={loading}>
                {loading ? t("book.form.booking") : t("book.form.submit")}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                {t("book.form.alreadyEnrolled")}{" "}
                <Link to="/auth" className="font-semibold text-accent hover:underline">
                  {t("book.form.parentSignIn")}
                </Link>
              </p>
            </form>
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
